import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { i3Assets } from '../utils/i3Assets';
import { useI3SiteContent } from '../cms/useI3SiteContent';
import type { I3SiteContentV1 } from '../cms/types';
import '../styles/I3Page.css';
import '../styles/I3AfterBooking.css';

/**
 * After Calendly booking → payment (Clover).
 *
 * UX:
 * - Calendly’s “redirect after booking” only runs when someone **finishes** scheduling, so
 *   people who abandon Calendly never hit this page from that flow.
 * - Use a **different redirect URL per event type**, with `?service=<card-id>` matching
 *   `site-content.json` service card ids. That way we open the correct Clover link.
 *
 * Calendly redirect examples (replace origin with production):
 * - .../after-booking?service=premarital-counseling
 * - .../after-booking?service=postmarital-counseling
 * - .../after-booking?service=counseling-1on1
 *
 * Query: `service` or `offering` — card id from CMS.
 * `?now=1` or `?delay=0` — skip countdown (only when a valid pay URL exists).
 */
const REDIRECT_MS = 1800;

function resolveCardForService(
  content: I3SiteContentV1,
  serviceId: string | null
): { title: string; payUrl: string } | null {
  if (!serviceId?.trim()) return null;
  for (const pillar of content.services.pillars) {
    for (const card of pillar.cards) {
      if (card.id === serviceId) {
        const payUrl =
          card.payUrl && card.payUrl.trim() !== ''
            ? card.payUrl
            : i3Assets.cloverPaymentUrl;
        return { title: card.title, payUrl };
      }
    }
  }
  return null;
}

const I3AfterBooking: React.FC = () => {
  const { content, ready } = useI3SiteContent();
  const [searchParams] = useSearchParams();
  const serviceId = searchParams.get('service') || searchParams.get('offering');
  const instant =
    searchParams.get('now') === '1' || searchParams.get('delay') === '0';

  const resolved = useMemo(
    () => resolveCardForService(content, serviceId),
    [content, serviceId]
  );

  const payUrl = resolved?.payUrl ?? null;
  const serviceTitle = resolved?.title;

  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(REDIRECT_MS / 1000));

  useEffect(() => {
    if (!ready || !payUrl) return;
    if (instant) {
      window.location.replace(payUrl);
      return;
    }
    const deadline = Date.now() + REDIRECT_MS;
    const tick = window.setInterval(() => {
      setSecondsLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    }, 250);
    const go = window.setTimeout(() => {
      window.location.replace(payUrl);
    }, REDIRECT_MS);
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(go);
    };
  }, [ready, payUrl, instant]);

  if (!ready) {
    return (
      <article className="i3-after-booking">
        <div className="i3-page-wrap i3-after-booking-inner">
          <p className="i3-after-booking-lead">Loading…</p>
        </div>
      </article>
    );
  }

  /* No service id — don’t send anyone to Clover automatically (bookmark / direct visit). */
  if (!serviceId?.trim()) {
    return (
      <article className="i3-after-booking">
        <div className="i3-page-wrap i3-after-booking-inner">
          <h1 className="i3-after-booking-title">Schedule first, then pay</h1>
          <p className="i3-after-booking-lead">
            This step is meant to open <strong>after</strong> you finish booking on Calendly. If you
            landed here without scheduling, choose a service and book — you&apos;ll be guided to
            payment right after.
          </p>
          <div className="i3-after-booking-actions">
            <Link to="/services" className="i3-cta i3-cta-primary">
              View services &amp; book
            </Link>
            <Link to="/contact" className="i3-cta i3-cta-secondary">
              Contact us
            </Link>
          </div>
        </div>
      </article>
    );
  }

  /* service= present but not found in CMS */
  if (!resolved) {
    return (
      <article className="i3-after-booking">
        <div className="i3-page-wrap i3-after-booking-inner">
          <h1 className="i3-after-booking-title">Something didn&apos;t match</h1>
          <p className="i3-after-booking-lead">
            We couldn&apos;t connect this link to a service. Go back to Services and book from
            there, or reach out and we&apos;ll help.
          </p>
          <div className="i3-after-booking-actions">
            <Link to="/services" className="i3-cta i3-cta-primary">
              Services
            </Link>
            <Link to="/contact" className="i3-cta i3-cta-secondary">
              Contact
            </Link>
          </div>
        </div>
      </article>
    );
  }

  if (instant && payUrl) {
    return (
      <article className="i3-after-booking">
        <div className="i3-page-wrap i3-after-booking-inner">
          <p className="i3-after-booking-lead">Taking you to payment…</p>
        </div>
      </article>
    );
  }

  return (
    <article className="i3-after-booking">
      <div className="i3-page-wrap i3-after-booking-inner">
        <h1 className="i3-after-booking-title">You&apos;re scheduled</h1>
        <p className="i3-after-booking-lead">
          {serviceTitle ? (
            <>
              Thanks for booking <strong>{serviceTitle}</strong>. Next, complete payment securely
              through Clover
            </>
          ) : (
            <>Next, complete payment securely through Clover</>
          )}
          {secondsLeft > 0 ? (
            <>
              {' '}
              — redirecting in <strong>{secondsLeft}</strong>s
            </>
          ) : (
            '…'
          )}
        </p>
        <p className="i3-after-booking-hint">
          If nothing happens, use the button below.
        </p>
        <div className="i3-after-booking-actions">
          <a href={payUrl!} className="i3-cta i3-cta-primary">
            Pay now (Clover)
          </a>
          <Link to="/services" className="i3-cta i3-cta-secondary">
            Back to services
          </Link>
        </div>
      </div>
    </article>
  );
};

export default I3AfterBooking;
